import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSignature, Download, Save, Home, Trees, Landmark, HandCoins } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fillContract } from "@/lib/fillContract";
import { DEAL_TYPES, getDealType, toPdfData, sanitizeNumber } from "@/lib/contractFields";

/** "123 Main St, Albuquerque, NM 87102" -> ["123 Main St", "Albuquerque, NM 87102"] */
export function splitAddress(full) {
  const parts = String(full || "").split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return [parts[0] || "", ""];
  return [parts[0], parts.slice(1).join(", ")];
}

const ICONS = { house: Home, land: Trees, subto: Landmark, seller_finance: HandCoins };

export default function CreateContract() {
  const [params, setParams] = useSearchParams();
  const typeId = params.get("type") || "house";
  const [dealId, setDealId] = useState(params.get("deal") || "");
  const [deals, setDeals] = useState([]);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const dealType = useMemo(() => getDealType(typeId) || DEAL_TYPES[0], [typeId]);
  const TypeIcon = ICONS[dealType.id];

  useEffect(() => {
    api.get("/deals").then(({ data }) => setDeals(data || [])).catch(() => {});
  }, []);

  // Load saved answers for this deal type (+ deal), falling back to prefill from the deal/profile.
  useEffect(() => {
    let active = true;
    setLoading(true);
    const q = { deal_type: typeId };
    if (dealId) q.deal_id = dealId;
    Promise.all([
      api.get("/contract-forms", { params: q }).then((r) => r.data?.fields || {}).catch(() => ({})),
      api.get("/auth/me/buyer-profile").then((r) => r.data || {}).catch(() => ({})),
    ]).then(([saved, profile]) => {
      if (!active) return;
      const deal = deals.find((d) => d.id === dealId);
      const prefill = {
        buyer_company_name: profile.company_name || "",
        buyer_signer_name: profile.signer_name || "",
        buyer_email: profile.email || "",
        buyer_phone: profile.phone || "",
        buyer_name: `${profile.company_name || profile.signer_name || ""} and/or assigns`.trim(),
        earnest_money: "1,000",
        inspection_days: "10",
        ...(deal
          ? {
              property_address_1: splitAddress(deal.property_address)[0],
              property_address_2: splitAddress(deal.property_address)[1],
              purchase_price: deal.contract_price ? String(deal.contract_price) : deal.asking_price ? String(deal.asking_price) : "",
            }
          : {}),
      };
      const merged = { ...prefill };
      Object.entries(saved).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") merged[k] = v;
      });
      setForm(merged);
      setLoading(false);
    });
    return () => { active = false; };
  }, [typeId, dealId, deals]);

  const update = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const save = useCallback(async (silent) => {
    await api.put("/contract-forms", { deal_id: dealId || null, deal_type: typeId, fields: form });
    if (!silent) toast.success("Answers saved", { description: "Come back any time to edit and re-create." });
  }, [dealId, typeId, form]);

  const create = async () => {
    const street = (form.property_address_1 || "").trim();
    if (!street) {
      toast.error("Street address is required");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`/contracts/${dealType.template}`);
      if (!res.ok) throw new Error("Template not found");
      const templateBytes = await res.arrayBuffer();
      const bytes = await fillContract(templateBytes, toPdfData(dealType, form));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: `Contract - ${street}.pdf` });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await save(true);
      toast.success("Contract created", { description: `Contract - ${street}.pdf is in your downloads.` });
    } catch (e) {
      toast.error("Could not create the contract", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="create-contract-page">
      <div className="mb-8">
        <div className="section-tag mb-3"><span className="dot" /> Paperwork</div>
        <h1 className="h-display text-4xl">Create Contract</h1>
        <p className="text-sm mt-3 flex items-center gap-2" style={{ color: "var(--text-muted)" }} data-testid="contract-type-label">
          <TypeIcon size={15} style={{ color: "var(--lime)" }} />
          <span><strong style={{ color: "var(--text)" }}>{dealType.label}</strong> — {dealType.blurb}</span>
        </p>
      </div>

      {deals.length > 0 && (
        <div className="surface-card p-5 mb-6">
          <label className="field-label">Attach to a deal (optional)</label>
          <select
            className="input-base"
            value={dealId}
            onChange={(e) => {
              setDealId(e.target.value);
              const next = new URLSearchParams(params);
              e.target.value ? next.set("deal", e.target.value) : next.delete("deal");
              setParams(next, { replace: true });
            }}
            data-testid="contract-deal-select"
          >
            <option value="">Not attached to a deal</option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.property_address} — submitted {new Date(d.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </option>
            ))}
          </select>
          <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
            Your answers are saved with the deal so you can edit them and create the contract again later.
          </p>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><span className="spinner" /></div>
      ) : (
        <div className="space-y-6">
          {dealType.sections.map((section) => {
            const fields = section.fields.filter((f) => !f.showIf || f.showIf(form));
            if (fields.length === 0) return null;
            return (
              <div key={section.title} className="surface-card p-6" data-testid={`contract-section-${section.title.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>{section.title}</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {fields.map((f) => (
                    <FieldInput key={f.name} field={f} value={form[f.name]} onChange={(v) => update(f.name, v)} />
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-3 pb-10">
            <button onClick={create} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="create-contract-button">
              {busy ? <span className="spinner" /> : <FileSignature size={15} />}
              {busy ? "Creating..." : "Create Contract"}
            </button>
            <button onClick={() => save(false)} className="btn-ghost flex items-center gap-2 text-sm" data-testid="save-contract-answers-button">
              <Save size={14} /> Save answers
            </button>
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
              <Download size={12} /> Downloads as “Contract - {form.property_address_1 || "street address"}.pdf”
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const testid = `contract-field-${field.name}`;

  if (field.kind === "pickone") {
    return (
      <div className="sm:col-span-2">
        <label className="field-label">{field.label}</label>
        <div className="flex flex-wrap gap-2" data-testid={testid}>
          {field.options.map((o) => {
            const active = value === o.field;
            return (
              <button
                key={o.field}
                type="button"
                onClick={() => onChange(o.field)}
                className="px-4 py-2 rounded-full text-xs font-medium transition"
                style={{
                  background: active ? "var(--lime-glow)" : "var(--surface2)",
                  color: active ? "var(--lime)" : "var(--text-muted)",
                  border: `1px solid ${active ? "var(--border-lime)" : "var(--border)"}`,
                }}
                data-testid={`contract-option-${o.field}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const isTextarea = field.kind === "textarea";
  return (
    <div className={isTextarea ? "sm:col-span-2" : undefined}>
      <label className="field-label">{field.label}</label>
      {isTextarea ? (
        <textarea
          className="input-base"
          rows={2}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : (
        <input
          className="input-base"
          type={field.kind === "date" ? "date" : field.type || "text"}
          inputMode={field.kind === "number" ? "decimal" : undefined}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.kind === "number" ? sanitizeNumber(e.target.value) : e.target.value)}
          data-testid={testid}
        />
      )}
      {field.help && (
        <p className="text-xs mt-1.5" style={{ color: "var(--text-dim)" }} data-testid={`${testid}-help`}>{field.help}</p>
      )}
    </div>
  );
}
